import React, { useCallback, useEffect, useState } from 'react';
import { useLocale } from '../../context/LocaleContext';
import { meetingService } from '../../services/meetingService';
import { FirstStepSignupConfig } from '../../types/workshopFirstStepSignup';
import { firstStepSignupCollapsedSummary, firstStepSignupShouldShow } from '../../utils/workshopFirstStepSignup';
import WorkshopCollapsibleCard from './WorkshopCollapsibleCard';
import WorkshopFirstStepSignup from './WorkshopFirstStepSignup';

interface WorkshopFirstStepSignupCardProps {
  meetingCode: string;
  canFacilitate?: boolean;
  defaultOpen?: boolean;
  nested?: boolean;
  initialSignup?: FirstStepSignupConfig | null;
  viewerUserId?: string;
}

const WorkshopFirstStepSignupCard: React.FC<WorkshopFirstStepSignupCardProps> = ({
  meetingCode,
  canFacilitate = false,
  defaultOpen = false,
  nested = false,
  initialSignup = null,
  viewerUserId,
}) => {
  const { t } = useLocale();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [config, setConfig] = useState<FirstStepSignupConfig | null>(initialSignup);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setConfig(initialSignup);
    }
  }, [initialSignup, isOpen]);

  const loadDetail = useCallback(async () => {
    setDetailLoading(true);
    try {
      const data = await meetingService.getFirstStepSignup(meetingCode);
      setConfig(data);
    } catch {
      setConfig(null);
    } finally {
      setDetailLoading(false);
    }
  }, [meetingCode]);

  useEffect(() => {
    if (!isOpen) return;
    void loadDetail();
  }, [isOpen, loadDetail]);

  const displayConfig = config ?? initialSignup ?? null;
  const subtitle = firstStepSignupCollapsedSummary(
    displayConfig,
    isOpen && detailLoading,
    viewerUserId
  );

  if (!firstStepSignupShouldShow(displayConfig)) {
    return null;
  }

  return (
    <WorkshopCollapsibleCard
      id="first-step-signup"
      title={t('workshop.2026.first_step.title')}
      subtitle={subtitle}
      defaultOpen={defaultOpen}
      open={isOpen}
      onOpenChange={setIsOpen}
      nested={nested}
    >
      <WorkshopFirstStepSignup
        meetingCode={meetingCode}
        canFacilitate={canFacilitate}
        onConfigChange={setConfig}
        parentManagesLoad
        config={config}
        loading={detailLoading}
        viewerUserId={viewerUserId}
      />
    </WorkshopCollapsibleCard>
  );
};

export default WorkshopFirstStepSignupCard;
